// Hover descriptions for the IDENT / CHASSIS cards. Pilot files store plain
// display names ("Vanguard II", "Hack or Fix +2"), so everything here is looked
// up by name across the core book, the supplements and any dropped-in LCPs.

import { all } from "@/utils/lancerData";

const catalog = {
	skills: all("skills"),
	talents: all("talents"),
	gear: all("pilot_gear"),
	frames: all("frames"),
	weapons: all("weapons"),
	systems: all("systems"),
	mods: all("mods"),
	bonds: all("bonds"),
	tags: all("tags"),
};

// "tg_ap" -> "AP" style labels for stat lines (falls back to the tag id)
const tagName = tag => {
	const known = catalog.tags.find(t => t.id === tag.id);
	const name = known
		? known.name.replace(/\s*\{VAL\}/i, "").replace(/^.*\(([^)]+)\)$/, "$1")
		: tag.id.replace(/^tg_/, "");
	return tag.val !== undefined ? `${name} ${tag.val}` : name;
};

const MAX_BODY = 700;

export function stripHtml(html) {
	return String(html || "")
		.replace(/<br\s*\/?>/gi, "\n")
		.replace(/<\/(p|li|div)>/gi, "\n")
		.replace(/<li>/gi, "• ")
		.replace(/<[^>]+>/g, "")
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
}

const clip = text => (text.length > MAX_BODY ? `${text.slice(0, MAX_BODY).trimEnd()}…` : text);
const byName = (list, name) => {
	const wanted = String(name || "")
		.trim()
		.toLowerCase();
	return list.find(item => String(item.name).toLowerCase() === wanted);
};
const info = (title, body) => (body ? { title, body: clip(stripHtml(body)) } : null);

// Rules text lives in `effect` for core-book items and in `actions[]` for some supplements
const rulesText = item =>
	[
		item.effect && stripHtml(item.effect),
		...(item.actions || [])
			.filter(Boolean)
			.map(a => `${a.name || a.activation || "Action"}: ${stripHtml(a.detail)}`),
	]
		.filter(Boolean)
		.join("\n\n");

// "Vanguard II" -> { name: "Vanguard", rank: 2 }
function splitRank(label) {
	const match = String(label).match(/^(.*?)\s+(I{1,3})$/);
	return match ? { name: match[1], rank: match[2].length } : { name: String(label), rank: 0 };
}

const cache = new Map();
const memo = (key, build) => {
	if (!cache.has(key)) cache.set(key, build());
	return cache.get(key);
};

export const lookup = {
	skill: label =>
		memo(`skill:${label}`, () => {
			const skill = byName(catalog.skills, String(label).replace(/\s*\+\d+$/, ""));
			return skill && info(label, skill.detail || skill.description);
		}),

	talent: label =>
		memo(`talent:${label}`, () => {
			const { name, rank } = splitRank(label);
			const talent = byName(catalog.talents, name);
			if (!talent) return null;
			const ranks = talent.ranks.slice(0, rank || talent.ranks.length);
			return info(
				label,
				ranks
					.map((r, i) => `${"I".repeat(i + 1)} · ${r.name}\n${stripHtml(r.description)}`)
					.join("\n\n")
			);
		}),

	// "SSC Emperor II" / "Emperor II" -> what each license rank unlocks
	license: label =>
		memo(`license:${label}`, () => {
			const { name, rank } = splitRank(label);
			const wanted = name.toLowerCase();
			const frame = catalog.frames.find(
				f => `${f.source} ${f.name}`.toLowerCase() === wanted || f.name.toLowerCase() === wanted
			);
			if (!frame) return null;
			// Core-book items link by license_id; supplements (KTB) link by license name
			const frameName = frame.name.toLowerCase();
			const inLicense = item =>
				item === frame ||
				(item.license_id && item.license_id === frame.license_id) ||
				String(item.license || "").toLowerCase() === frameName;
			const licensed = [
				...catalog.frames,
				...catalog.weapons,
				...catalog.systems,
				...catalog.mods,
			].filter(
				item => inLicense(item) && item.license_level > 0 && item.license_level <= (rank || 3)
			);
			const lines = [1, 2, 3]
				.filter(level => level <= (rank || 3))
				.map(level => {
					const names = licensed
						.filter(item => item.license_level === level)
						.map(item => item.name);
					return names.length ? `${"I".repeat(level)} · ${names.join(", ")}` : "";
				})
				.filter(Boolean);
			return info(label, lines.join("\n"));
		}),

	gear: label =>
		memo(`gear:${label}`, () => {
			const item = byName(catalog.gear, label);
			if (!item) return null;
			// Pilot weapons carry a profile: range, damage and tags
			const profile =
				item.damage || item.range
					? [
							(item.range || []).map(r => `${r.type} ${r.val}`).join(", "),
							(item.damage || []).map(d => `${d.val} ${d.type}`).join(" + "),
							(item.tags || [])
								.filter(t => t.id !== "tg_pilot_weapon")
								.map(tagName)
								.join(", "),
					  ]
							.filter(Boolean)
							.join(" // ")
					: "";
			return info(label, [profile, rulesText(item), item.description].filter(Boolean).join("\n\n"));
		}),

	weapon: label =>
		memo(`weapon:${label}`, () => {
			const weapon = byName(catalog.weapons, label);
			if (!weapon) return null;
			const profile = [
				[weapon.mount, weapon.type].filter(Boolean).join(" "),
				(weapon.damage || []).map(d => `${d.val} ${d.type}`).join(" + "),
				(weapon.range || []).map(r => `${r.type} ${r.val}`).join(", "),
			]
				.filter(Boolean)
				.join(" // ");
			const effect = [weapon.on_hit && `On hit: ${stripHtml(weapon.on_hit)}`, rulesText(weapon)]
				.filter(Boolean)
				.join("\n\n");
			return info(
				label,
				[profile, effect || stripHtml(weapon.description)].filter(Boolean).join("\n\n")
			);
		}),

	system: label =>
		memo(`system:${label}`, () => {
			const system = byName(catalog.systems, label);
			return system && info(label, rulesText(system) || system.description);
		}),

	trait: label =>
		memo(`trait:${label}`, () => {
			const trait = catalog.frames
				.flatMap(f => f.traits || [])
				.find(t => t.name.toLowerCase() === String(label).toLowerCase());
			return trait && info(label, trait.description);
		}),

	core: label =>
		memo(`core:${label}`, () => {
			const core = catalog.frames
				.map(f => f.core_system)
				.find(c => c && c.name.toLowerCase() === String(label).toLowerCase());
			if (!core) return null;
			return info(
				label,
				[
					core.active_name && `${core.active_name}: ${stripHtml(core.active_effect)}`,
					stripHtml(core.description),
				]
					.filter(Boolean)
					.join("\n\n")
			);
		}),
};

// "Wolf" or "The Wolf" -> the KTB bond definition
export function findBond(name) {
	const wanted = String(name || "")
		.trim()
		.toLowerCase()
		.replace(/^the\s+/, "");
	return catalog.bonds.find(b => b.name.toLowerCase().replace(/^the\s+/, "") === wanted) || null;
}
